import { Intersection } from "../Intersection.js";
import { errDiv, sumValues } from "../../../util/mathHelpers.ts";

// const DEBUG = false;

/** All Way Stop Control computational class. Extends the Intersection parent class */
export class AllWayStopControl extends Intersection {
  /**
   * Constructor for the NewIntxTemplate class.
   * @param {string} name - Name of the intersection
   * @param {Object} volumes - Object mapping direction strings (eastbound, westbound, northbound, southbound) to  {@link Volume} objects.
   * @param {Object} globalParams - Object containing the required global parameters for an intersection.
   */
  constructor(name, volumes, globalParams) {
    super(name, volumes, globalParams);
    this.LaneConfig = AllWayStopControl.getZoneDefaultInputs();
  }

  /**
   * Function to get the DEFAULT inputs available the intersection.  This function is designed to facilitate the
   * integration of the engine into a user interface.
   *
   * An AWSC has a single zone (Z5) and is independent of Major/Minor street designation, so the four
   * cardinal directions are used.
   *
   * @return {Object} Object representation of default inputs
   */
  static getZoneDefaultInputs() {
    const defaultLaneConfig = {
      LT: 0,
      T: 1,
      RT: 0,
    };
    return {
      Z5: {
        EB: { ...defaultLaneConfig },
        WB: { ...defaultLaneConfig },
        NB: { ...defaultLaneConfig },
        SB: { ...defaultLaneConfig },
      },
    };
  }

  static getLaneConfigLabelsMapping() {
    return {
      Z5: this.getLabelsMappingWithoutMajorDir(),
    };
  }

  setLaneConfigInputs(laneConfigInputs) {
    this.LaneConfig = laneConfigInputs;
  }

  // Implements the computeVCAnalysis function of the Intersection parent class.
  _runCriticalMovementAnalysis() {
    const EBVolume = this.EBL_MASTER + this.EBT_MASTER + this.EBR_MASTER;
    const WBVolume = this.WBL_MASTER + this.WBT_MASTER + this.WBR_MASTER;
    const NBVolume = this.NBL_MASTER + this.NBT_MASTER + this.NBR_MASTER;
    const SBVolume = this.SBL_MASTER + this.SBT_MASTER + this.SBR_MASTER;
    const CLV = EBVolume + WBVolume + NBVolume + SBVolume;

    // veh/h capacity, depends on number of lanes
    const totalEnteringCapacity = this.getTotalEnteringCapacity();

    // Assign results for each zone
    this._resultsByZone = {
      Z5: {
        VC: errDiv(CLV, totalEnteringCapacity),
        CLV: CLV,
      },
    };
  }

  getTotalEnteringCapacity() {
    const EBLanes = sumValues(this.LaneConfig.Z5.EB);
    const WBLanes = sumValues(this.LaneConfig.Z5.WB);
    const NBLanes = sumValues(this.LaneConfig.Z5.NB);
    const SBLanes = sumValues(this.LaneConfig.Z5.SB);

    if (EBLanes <= 1 && WBLanes <= 1 && NBLanes <= 1 && SBLanes <= 1) {
      // both streets: 1-lane approach
      return 1200;
    }
    if (EBLanes >= 2 && WBLanes >= 2 && NBLanes >= 2 && SBLanes >= 2) {
      // both streets: 2-lane approach
      return 1800;
    }
    // one street: 1-lane; one street: 2-lane
    return 1500;
  }
}
